import { type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  BaseButton,
  BaseInput,
  rubikTypographyStyles,
} from '../../../shared/ui';

type TipCheckoutAmountOption = {
  amount: number;
  label: string;
};

type TipCheckoutAmountCardProps = {
  amountOptions: ReadonlyArray<TipCheckoutAmountOption>;
  currencyPrefix: string;
  isManualAmountEntry: boolean;
  manualAmountInput: string;
  selectedAmount: number | null;
  selectedAmountLabel: string;
  onAmountSelect: (amount: number) => void;
  onManualAmountActivate: () => void;
  onManualAmountChange: (nextValue: string) => void;
  onManualAmountSubmit: () => void;
};

const AmountCard = styled.article.attrs({
  'data-ui': 'tips-checkout-amount-card',
})`
  background: ${({ theme }) => theme.colors.surface.subtle};
  border-radius: ${({ theme }) => theme.scale.px(11)};
  padding: ${({ theme }) => theme.scale.px(15)}
    ${({ theme }) => theme.scale.px(19)} ${({ theme }) => theme.scale.px(20)};
  display: grid;
  justify-items: center;
  gap: ${({ theme }) => theme.scale.px(12)};
`;

const AmountTitle = styled.p`
  ${rubikTypographyStyles};
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.scale.px(8)};
  width: 100%;
  font-size: ${({ theme }) => theme.scale.px(32)};
  font-weight: 600;
  line-height: 1.18;

  & > span {
    color: ${({ theme }) => theme.colors.border.emphasis};
  }
`;

const InsertValueTag = styled(BaseButton)<{ $isHidden: boolean }>`
  ${rubikTypographyStyles};
  min-height: ${({ theme }) => theme.scale.px(24)};
  border-radius: ${({ theme }) => theme.scale.px(7)};
  background: ${({ theme }) => theme.colors.brand.primary};
  padding: 0 ${({ theme }) => theme.scale.px(13)};
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 600;
  line-height: 1.18;
  visibility: ${({ $isHidden }) => ($isHidden ? 'hidden' : 'visible')};
  pointer-events: ${({ $isHidden }) => ($isHidden ? 'none' : 'auto')};
`;

const ManualAmountInput = styled(BaseInput)`
  ${rubikTypographyStyles};
  width: ${({ theme }) => theme.scale.px(132)};
  max-width: 100%;
  font-size: ${({ theme }) => theme.scale.px(32)};
  font-weight: 600;
  line-height: 1.18;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const OptionsRow = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: ${({ theme }) => theme.scale.px(7)};
`;

const AmountOptionButton = styled(BaseButton)<{ $isSelected: boolean }>`
  ${rubikTypographyStyles};
  min-height: ${({ theme }) => theme.scale.px(35)};
  border-radius: ${({ theme }) => theme.scale.px(7)};
  background: ${({ $isSelected, theme }) =>
    $isSelected ? 'rgba(255, 190, 0, 0.5)' : 'transparent'};
  font-size: ${({ theme }) => theme.scale.px(11)};
  font-weight: ${({ $isSelected }) => ($isSelected ? 600 : 400)};
  line-height: 1.21;
  white-space: nowrap;
`;

export function TipCheckoutAmountCard({
  amountOptions,
  currencyPrefix,
  isManualAmountEntry,
  manualAmountInput,
  selectedAmount,
  selectedAmountLabel,
  onAmountSelect,
  onManualAmountActivate,
  onManualAmountChange,
  onManualAmountSubmit,
}: TipCheckoutAmountCardProps) {
  const { t } = useTranslation();
  const insertValueLabel = t('registrationComplete.insertValueLabel');

  const handleManualAmountKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    onManualAmountSubmit();
  };

  return (
    <AmountCard>
      <AmountTitle>
        <span>{currencyPrefix.trim()}</span>
        {isManualAmountEntry ? (
          <ManualAmountInput
            aria-label={insertValueLabel}
            autoFocus
            inputMode="numeric"
            pattern="[0-9]*"
            type="text"
            value={manualAmountInput}
            onChange={event => onManualAmountChange(event.target.value)}
            onBlur={onManualAmountSubmit}
            onKeyDown={handleManualAmountKeyDown}
          />
        ) : (
          selectedAmountLabel
        )}
      </AmountTitle>

      <InsertValueTag
        $isHidden={isManualAmountEntry}
        type="button"
        onClick={onManualAmountActivate}
      >
        {insertValueLabel}
      </InsertValueTag>

      <OptionsRow>
        {amountOptions.map(option => (
          <AmountOptionButton
            $isSelected={option.amount === selectedAmount}
            aria-pressed={option.amount === selectedAmount}
            key={option.amount}
            type="button"
            onClick={() => onAmountSelect(option.amount)}
          >
            {option.label}
          </AmountOptionButton>
        ))}
      </OptionsRow>
    </AmountCard>
  );
}
